import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#D47A65", "#94A68D", "#264D4F", "#E29882", "#7A8E73", "#8F4B3D"];

export default function AdminAnalytics() {
  const { lang } = useLang();
  const [instruments, setInstruments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/instruments").then(r => { setInstruments(r.data); if (r.data.length > 0) setSelected(r.data[0].id); });
  }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/admin/analytics/${selected}`).then(r => setData(r.data));
  }, [selected]);

  const genderData = data ? Object.entries(data.demographics.gender).map(([k, v]) => ({ name: k, value: v })) : [];
  const ageData = data ? Object.entries(data.demographics.age_buckets).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "Analitik Respons" : "Response Analytics"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{lang === "id" ? "Distribusi & tren per instrumen." : "Distributions & trends per instrument."}</p>
        </div>
        <Select value={selected || ""} onValueChange={setSelected}>
          <SelectTrigger className="w-72" data-testid="analytics-instrument-select"><SelectValue placeholder={lang === "id" ? "Pilih instrumen" : "Select instrument"} /></SelectTrigger>
          <SelectContent>
            {instruments.map(i => (<SelectItem key={i.id} value={i.id}>{lang === "id" ? i.name_id : i.name_en}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {!data ? (
        <div className="text-muted-foreground text-sm">…</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label={lang === "id" ? "Total Selesai" : "Total Completed"} value={data.total_completed} testid="analytics-total" />
            <Stat label={lang === "id" ? "Dimensi" : "Dimensions"} value={data.dimensions.length} testid="analytics-dims" />
            <Stat label={lang === "id" ? "Rata-rata / dim" : "Avg / dim"} value={data.dimensions.length ? (data.dimensions.reduce((a, d) => a + d.mean, 0) / data.dimensions.length).toFixed(2) : "-"} testid="analytics-avg" />
            <Stat label={lang === "id" ? "Hari aktif" : "Active days"} value={data.timeseries.length} testid="analytics-days" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card title={lang === "id" ? "Rata-rata skor per dimensi" : "Mean score per dimension"}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.dimensions.map(d => ({ name: lang === "id" ? d.name_id : d.name_en, mean: d.mean }))}>
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="mean" fill="#D47A65" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title={lang === "id" ? "Respons per hari" : "Responses per day"}>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.timeseries}>
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#264D4F" strokeWidth={2} dot={{ fill: "#D47A65" }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card title={lang === "id" ? "Distribusi jenis kelamin" : "Gender distribution"}>
              {genderData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={3}>
                      {genderData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-sm text-muted-foreground py-10 text-center">{lang === "id" ? "Belum ada data." : "No data."}</div>}
            </Card>
            <Card title={lang === "id" ? "Distribusi usia" : "Age distribution"}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={ageData}>
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#94A68D" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card title={lang === "id" ? "Distribusi kategori per dimensi" : "Category distribution per dimension"}>
            <div className="grid md:grid-cols-2 gap-4">
              {data.dimensions.map(d => (
                <div key={d.dimension_id} className="p-4 rounded-xl bg-secondary/40 border border-border">
                  <div className="font-display text-sm mb-2">{lang === "id" ? d.name_id : d.name_en}</div>
                  <div className="text-xs text-muted-foreground mb-3">n={d.count} · mean={d.mean} · sd={d.sd}</div>
                  <div className="space-y-1.5">
                    {Object.entries(d.category_counts).map(([k, v]) => {
                      const pct = d.count ? (v / d.count) * 100 : 0;
                      return (
                        <div key={k} className="flex items-center gap-2 text-xs">
                          <div className="w-20 truncate">{k}</div>
                          <div className="flex-1 h-2 rounded-full bg-cream-100 overflow-hidden">
                            <div className="h-full bg-terracotta-500" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="w-8 text-right text-muted-foreground">{v}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="p-5 rounded-2xl bg-card border border-border" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-display text-3xl mt-1">{value}</div>
  </div>
);
const Card = ({ title, children }) => (
  <div className="p-5 rounded-2xl bg-card border border-border">
    <div className="font-display text-lg mb-3">{title}</div>
    {children}
  </div>
);
